/**
 * D4's own rules (spec §4.2, §4.4, §4.5): Google Search and Shopping are left out; Active reads the campaign; the
 * status chip reads the ad set and the campaign (R2: the two state rules are never swapped). Pure.
 *
 * @module services/audience/audienceRules
 */
import { campaignStateOf, isActiveState } from '@/services/channels/campaignState';
import { LEADING_TAGS } from '@/services/channels/channelNaming';
import type { CampaignState } from '@/services/channels/channelTypes';
import { creativeStateOf } from '@/services/creatives/creativeRows';
import { norm } from '@/services/looker/campaignNaming';
import type { AudienceFact } from './audienceTypes';

/** The first campaign segments §4.2 leaves out, as norm writes them: Google Search (SEM) and Shopping (GSA). */
const SEARCH_AND_SHOPPING: ReadonlySet<string> = new Set(['sem', 'gsa']);

/**
 * Spec §4.2: a Google ad group whose campaign name, with every leading "[…]" tag stripped (D2's LEADING_TAGS, from
 * D2's plan R13), has a first "|" segment that norm reduces to sem or gsa. A later segment does not count; other
 * channels never do.
 */
export const isSearchOrShopping = (f: Pick<AudienceFact, 'platform' | 'campaignName'>): boolean =>
  f.platform === 'google' && SEARCH_AND_SHOPPING.has(norm(f.campaignName.replace(LEADING_TAGS, '').split('|')[0]));

/** R2, spec §4.4: Active reads the campaign alone (D2's rule), so an ad set paused in an Active campaign stays. */
export const isActiveCampaign = (f: Pick<AudienceFact, 'campaignStatus' | 'campaignEndDate'>, yesterday: string): boolean =>
  isActiveState(campaignStateOf({ campaignStatus: f.campaignStatus, campaignEndDate: f.campaignEndDate }, yesterday));

/** R2, spec §4.5: the chip reads the ad set and the campaign, D3's order with the ad set in the ad's place. */
export const audienceStateOf = (
  f: Pick<AudienceFact, 'adSetStatus' | 'campaignStatus' | 'campaignEndDate'>, yesterday: string,
): CampaignState =>
  creativeStateOf({ adStatus: f.adSetStatus, campaignStatus: f.campaignStatus, campaignEndDate: f.campaignEndDate }, yesterday);

/** R4: the page draws a chip for ended and paused only: Active is the page's default, and unknown has no word. */
export const hasStateChip = (s: CampaignState): boolean => s.kind === 'ended' || s.kind === 'paused';
